"use client";

import React from "react";
import { Target, Compass, Layers, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const focusPoints = [{
  title: "HST (13%)",
  description: "The Harmonized Sales Tax combines the 5% federal GST with Ontario's 8% provincial PST.",
  icon: Target
}, {
  title: "TFSA Limits",
  description: "Annual contribution limits are set by the CRA and accumulate from the year you turned 18.",
  icon: Layers
}, {
  title: "Rent Caps",
  description: "Ontario's annual rent increase guideline is based on the Ontario Consumer Price Index.",
  icon: Compass
}, {
  title: "Land Transfer",
  description: "Calculated on a sliding scale based on the purchase price of your Ontario property.",
  icon: Zap
}];
export default function DajamsApproach() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="225569" id="tax-rules-explained" ref={sectionRef} className="relative overflow-hidden bg-background py-24 lg:py-32 text-foreground">
      {}
      <div className={`absolute -right-[20%] top-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-[0.05] pointer-events-none transition-all duration-1000 ease-out ${sectionVisible ? 'scale-100' : 'scale-95'}`} aria-hidden="true">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1668301/img/ontario_tax_seal_watermark.png" alt="" className="w-full h-full object-contain" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          
          {}
          <div ref={leftRef} className={`lg:col-span-5 lg:sticky lg:top-32 transition-all duration-700 ease-out ${leftVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-primary font-bold tracking-widest uppercase text-sm mb-6 block font-default">
              Guidelines
            </span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-default font-light text-foreground leading-[1.1] tracking-tighter">
              Calculation<br />
              <span className="font-bold text-primary">Rules</span>
            </h2>
            <div className="w-20 h-1 bg-primary mt-8 rounded-md"></div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 delay-200 ease-out ${rightVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="prose prose-lg dark:prose-invert max-w-none mb-16">
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-default font-light">
                Understanding Ontario's tax regulations is the first step to accurate financial planning. At FINCALC, we break down technical complexity into simple tools.
              </p>
              <p className="text-lg text-muted-foreground/80 leading-relaxed font-default mt-6">
                From the breakdown of the 13% HST to the cumulative TFSA limits, we provide the logical basis behind each calculation to ensure your financial decisions are sound and legal.
              </p>
            </div>

            {}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {focusPoints.map((point, i) => <div data-index={i} key={i} className="h-full">
                  <Card className="h-full bg-card backdrop-blur-sm border-border shadow-md hover:shadow-lg transition-shadow duration-300 rounded-md">
                    <CardHeader className="pb-2 flex flex-row items-center gap-4">
                      <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                        <point.icon className="w-5 h-5 text-primary" />
                      </div>
                      <CardTitle className="text-lg font-default font-bold m-0">{point.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm font-default leading-relaxed">
                        {point.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </div>

        </div>
      </div>
    </section>;
}